import * as THREE from 'three';
import type { PiecePrim } from './bodyModels';
import { bodyShapeMorphs } from './bodyShapeMorphs';
import { MALE_RIG } from './maleRig';
import { FEMALE_RIG } from './femaleRig';
import { rigProfile } from './rigProfile';

/** Baked head metadata. Scales are relative changes; eyeOffset is meters per side. */
export type HeadFit = { neckScale: [number, number]; eyeOffset: number };
const smooth = (a: number, b: number, x: number): number => {
  const t = THREE.MathUtils.clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};

/** Matches the collar authored in Blender. No facial vertices are changed here. */
export function fitNeckPoint(p: THREE.Vector3, scale: readonly number[], cut: number): THREE.Vector3 {
  const { x, y, z } = p;
  const w = smooth(cut - .120, cut - .035, y) * (1 - smooth(cut + .010, cut + .040, y))
    * (1 - smooth(.095, .160, Math.abs(x)));
  return p.set(x * (1 + scale[0] * w), y, -.005 + (z + .005) * (1 + scale[1] * w));
}

const fitted = new WeakMap<THREE.BufferGeometry, Map<string, THREE.BufferGeometry>>();

/** A shared, immutable torso template for each authored collar. Recomputed body
 * volume targets use the same field as the head exporter, including at the cut.
 * Avatar instances still own their paint/index buffers, as for the base torso. */
export function fitBodyNeck(prims: PiecePrim[], fit: HeadFit | undefined, body: 'male' | 'female'): PiecePrim[] {
  if (!fit || fit.neckScale.every(v => v === 0)) return prims;
  const cut = body === 'male' ? 1.475 : 1.485;
  const key = `${body}:${fit.neckScale.join(',')}`;
  const table = body === 'male' ? MALE_RIG : FEMALE_RIG;
  const profile = rigProfile(table), world = new Map(table.map(([name], i) => [name, profile.world[i]]));
  return prims.map(prim => {
    const source = prim.geometry;
    let cache = fitted.get(source);
    const hit = cache?.get(key);
    if (hit) return { ...prim, geometry: hit };
    const p = source.getAttribute('position');
    const active: number[] = [];
    const v = new THREE.Vector3(), q = new THREE.Vector3();
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i); q.copy(v);
      if (fitNeckPoint(q, fit.neckScale, cut).distanceToSquared(v) > 1e-16) active.push(i);
    }
    if (!active.length) return prim;
    const geo = source.clone(), pos = geo.getAttribute('position'), norm = geo.getAttribute('normal');
    // Inverse-transpose Jacobian transports the authored normals without
    // smoothing away the existing low-poly planes or changing normal splits.
    const columns = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    const jacobian = new THREE.Matrix3(), normalMatrix = new THREE.Matrix3();
    const h = 1e-5;
    const transportNormal = (point: THREE.Vector3, normal: THREE.Vector3) => {
      for (let axis = 0; axis < 3; axis++) {
        q.copy(point).setComponent(axis, point.getComponent(axis) - h); fitNeckPoint(q, fit.neckScale, cut);
        columns[axis].copy(point).setComponent(axis, point.getComponent(axis) + h);
        fitNeckPoint(columns[axis], fit.neckScale, cut).sub(q).multiplyScalar(.5 / h);
      }
      const [a, b, c] = columns;
      jacobian.set(a.x, b.x, c.x, a.y, b.y, c.y, a.z, b.z, c.z);
      normalMatrix.copy(jacobian).invert().transpose();
      return normal.applyMatrix3(normalMatrix).normalize();
    };
    const endpoint = new THREE.Vector3(), endpointNormal = new THREE.Vector3(), fittedPoint = new THREE.Vector3();
    for (const i of active) {
      v.fromBufferAttribute(p, i);
      q.copy(v); fitNeckPoint(q, fit.neckScale, cut); pos.setXYZ(i, q.x, q.y, q.z);
      endpointNormal.fromBufferAttribute(source.getAttribute('normal'), i);
      transportNormal(v, endpointNormal); norm.setXYZ(i, endpointNormal.x, endpointNormal.y, endpointNormal.z);
      // Transform both ends of each authored build. Moving just the basis leaves
      // the broader collars with stale deltas and lighting at the head seam.
      source.morphAttributes.position?.forEach((delta, mi) => {
        if (!delta.name.startsWith('build_')) return;
        endpoint.fromBufferAttribute(delta, i).add(v);
        fittedPoint.copy(endpoint); fitNeckPoint(fittedPoint, fit.neckScale, cut);
        geo.morphAttributes.position[mi].setXYZ(i, fittedPoint.x - pos.getX(i), fittedPoint.y - pos.getY(i), fittedPoint.z - pos.getZ(i));
        const nd = source.morphAttributes.normal?.[mi];
        if (nd && geo.morphAttributes.normal?.[mi]) {
          endpointNormal.fromBufferAttribute(nd, i).add(new THREE.Vector3().fromBufferAttribute(source.getAttribute('normal'), i));
          transportNormal(endpoint, endpointNormal);
          geo.morphAttributes.normal[mi].setXYZ(i, endpointNormal.x - norm.getX(i), endpointNormal.y - norm.getY(i), endpointNormal.z - norm.getZ(i));
        }
      });
    }
    const shapes = bodyShapeMorphs(pos.array as Float32Array, world, body, norm.array as Float32Array);
    for (const shape of shapes) {
      const i = geo.morphAttributes.position?.findIndex(a => a.name === shape.name) ?? -1;
      if (i >= 0) {
        const attr = new THREE.BufferAttribute(shape.delta, 3); attr.name = shape.name;
        geo.morphAttributes.position[i] = attr;
      }
    }
    geo.computeBoundingBox(); geo.computeBoundingSphere();
    geo.userData.sharedTemplate = true;
    if (!cache) fitted.set(source, cache = new Map());
    cache.set(key, geo);
    return { ...prim, geometry: geo };
  });
}

const rotated = new THREE.Vector3();
/** Conjugate the eye rotation around its authored center while retaining the
 * shared bind matrices: T(rest + d - R*d) R. At neutral this is exactly rest. */
export function fitEyePivot(bone: THREE.Bone, rest: THREE.Vector3, offset: THREE.Vector3): void {
  rotated.copy(offset).applyQuaternion(bone.quaternion);
  bone.position.copy(rest).add(offset).sub(rotated);
}
